import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { formatDate } from '../../lib/format';
import { ECHO_SECTIONS, examDate, filled, operators, patientName, valueOf } from './echoModel';

// Same columns, in the same order, as the former export (spreadsheets built on it keep
// working); the heart rate and Colombo are now filled, and the patient file is added at the end.
const EXCEL_COLUMNS = [
  ['Nom', 'identite', 'nom'], ['Prénom', 'identite', 'prenom'], ['Date de naissance', 'identite', 'dateNaissance'],
  ['Adresse', 'identite', 'adresse'], ['Téléphone', 'identite', 'telephone'], ['Age', 'identite', 'age'],
  ['Sexe', 'identite', 'sexe'], ['Poids', 'identite', 'poids'], ['Taille', 'identite', 'taille'],
  ['Surface Corporelle', 'identite', 'surfaceCorporelle'], ['Diagnostic', 'identite', 'diagnostic'], ['TA', 'identite', 'ta'],
  ['FC', 'identite', 'FC'], ['SpO2', 'identite', 'spo2'], ['Température', 'identite', 'temperature'],
  ['Service Demandeur', 'identite', 'serviceDemandeur'], ['Opérateur', 'identite', 'operateur'],
  ['DVG Diastolique', 'mensuration', 'dvgDiastolique'], ['DVG Systolique', 'mensuration', 'dvgSystolique'],
  ['Septum Diastolique', 'mensuration', 'septumDiastolique'], ['Septum Systolique', 'mensuration', 'septumSystolique'],
  ['Paroi Post. Diastolique', 'mensuration', 'paroiPostDiastolique'], ['Péricarde', 'mensuration', 'pericarde'],
  ['DOG', 'mensuration', 'dog'], ['DAO', 'mensuration', 'dao'], ['DVD', 'mensuration', 'dvd'],
  ['Raccourcissement', 'mensuration', 'raccourcissement'], ['Fraction Ejection', 'mensuration', 'fractionEjection'],
  ['TAPSE', 'mensuration', 'tapse'], ['VCI Inspiration', 'mensuration', 'vciInsp'], ['VCI Expiration', 'mensuration', 'vciExp'],
  ['SOG', 'mensuration', 'sog'], ['SOD', 'mensuration', 'sod'],
  ['Surface Mitrale Planimétrie', 'mitrale', 'surfaceMitralePlanimetrie'], ['Surface Mitrale PHT', 'mitrale', 'surfaceMitralePHT'],
  ['IM Grade', 'mitrale', 'imGrade'], ['VC Mitrale', 'mitrale', 'vc'], ['SOR Mitrale', 'mitrale', 'sor'], ['VR Mitrale', 'mitrale', 'vr'],
  ['Gradient Max Mitral', 'mitrale', 'gradientMax'], ['Gradient Moyen Mitral', 'mitrale', 'gradientMoyen'],
  ['Onde E', 'mitrale', 'ondeE'], ['Onde A', 'mitrale', 'ondeA'], ['E/A', 'mitrale', 'ea'], ["Onde E'", 'mitrale', 'ondeEa'],
  ["Onde A'", 'mitrale', 'ondeAa'], ["E/A'", 'mitrale', 'eAa'], ['TRIV', 'mitrale', 'triv'], ['Anneau Mitral', 'mitrale', 'anneau'],
  ['IA Grade', 'aorte', 'iaGrade'], ['VC Aortique', 'aorte', 'vc'], ['SOR Aortique', 'aorte', 'sor'], ['VR Aortique', 'aorte', 'vr'],
  ['PHT Aortique', 'aorte', 'pht'], ['Reflux Crosse', 'aorte', 'refluxCrosse'], ['Gradient Max Aortique', 'aorte', 'gradientMax'],
  ['Gradient Moyen Aortique', 'aorte', 'gradientMoyen'], ['Vmax Aortique', 'aorte', 'vmax'], ['Surface Aortique', 'aorte', 'surfaceAortique'],
  ['Val Salva', 'aorte', 'valSalva'], ['JST', 'aorte', 'jst'], ['Aorte Ascendante', 'aorte', 'aorteAscendante'],
  ['Aorte Descendante', 'aorte', 'aorteDescendante'], ['Aorte Abdominale', 'aorte', 'aorteAbdominale'], ['Anneau Aortique', 'aorte', 'anneau'],
  ['IT Grade', 'tricuspide', 'itGrade'], ['Gradient VD-OD', 'tricuspide', 'gradientVdOd'], ['POD', 'tricuspide', 'pod'],
  ['PAPS', 'tricuspide', 'paps'], ['PAPD', 'tricuspide', 'papd'], ['PAP Moyenne', 'tricuspide', 'papMoy'],
  ['Anneau Tricuspide', 'tricuspide', 'anneau'], ['Colombo', 'tricuspide', 'Colombo'],
  ['IP Grade', 'pulmonaire', 'ipGrade'], ['Gradient Max Pulmonaire', 'pulmonaire', 'gradientMax'],
  ['Gradient Moyen Pulmonaire', 'pulmonaire', 'gradientMoyen'], ['Vmax Pulmonaire', 'pulmonaire', 'vmax'],
  ['Anneau Pulmonaire', 'pulmonaire', 'anneau'], ['APD', 'pulmonaire', 'apd'], ['APG', 'pulmonaire', 'apg'], ['Tronc', 'pulmonaire', 'tronc'],
  ['Commentaires', 'conclusion', 'commentaire'], ['Résumé', 'conclusion', 'resume'],
];

const cell = (value) => (Array.isArray(value) ? value.map((item) => String(item).trim()).join(', ') : value);

export const exportEchosExcel = (echos, patientsById = {}) => {
  const rows = echos.map((echo) => {
    const row = { 'Date de création': formatDate(echo.dateCreation) };
    EXCEL_COLUMNS.forEach(([label, group, name]) => {
      row[label] = cell(valueOf(echo, group, name));
    });
    const patient = patientsById[echo.patientId];
    row["Date de l'examen"] = formatDate(examDate(echo));
    row['Dossier patient'] = patient ? `${patient.nom}${patient.dossierNumber ? ` (${patient.dossierNumber})` : ''}` : '';
    return row;
  });
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Échos');
  XLSX.writeFile(book, 'echos_export.xlsx');
};

// One report per page; long sections continue on the next page instead of being cut.
export const exportEchosPdf = (echos) => {
  const pdf = new jsPDF();
  echos.forEach((echo, index) => {
    if (index > 0) pdf.addPage();
    let y = 20;
    const write = (text, { x = 18, size = 11, bold = false, gap = 6 } = {}) => {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      // The standard PDF font has no subscript digits (SpO₂).
      const lines = pdf.splitTextToSize(String(text).replace(/₂/g, '2'), 190 - x);
      lines.forEach((line) => {
        if (y > 282) {
          pdf.addPage();
          y = 20;
        }
        pdf.text(line, x, y);
        y += gap;
      });
    };
    write(`Échocardiographie — ${patientName(echo)}`, { x: 14, size: 16, bold: true, gap: 8 });
    write(`Examen du ${formatDate(examDate(echo)) || 'date non précisée'}${operators(echo).length ? ` · ${operators(echo).join(', ')}` : ''}`, { x: 14, size: 10, gap: 10 });
    ECHO_SECTIONS.forEach((section) => {
      const lines = section.fields
        .map((field) => [field, valueOf(echo, section.group, field.name)])
        .filter(([, value]) => filled(value));
      if (!lines.length) return;
      y += 2;
      write(section.title, { x: 14, size: 13, bold: true, gap: 7 });
      lines.forEach(([field, value]) => {
        const shown = Array.isArray(value) ? value.map((item) => String(item).trim()).join(', ') : field.name === 'sexe' ? (value === 'F' ? 'Féminin' : value === 'M' ? 'Masculin' : value) : value;
        write(`${field.label} : ${shown}${field.unit && field.type !== 'textarea' ? ` ${field.unit}` : ''}`);
      });
    });
  });
  pdf.save('echos_export.pdf');
};
